import { AggregateResultDto, EditDto, ScreenConfigDto, UseCaseController } from '@siza-staff-care/shared/data';
import { CrudService } from '@siza-staff-care/shared/global-services';
import { Observable } from 'rxjs';

export interface ICrudAdaptor {
	/**
	 * Runs the screen "open" function on the server's Jade controller, returning all required data
	 * to run the screen, including lookups and other config data.
	 * @param type The Jade controller class name.
	 * @param lookupTypes The lookup types to request.
	 * @param opts Additional options for opening the screen.
	 * @returns
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	open<T extends ScreenConfigDto = ScreenConfigDto, TController = UseCaseController>(
		type: string,
		lookupTypes?: string[],
		opts?: TController | undefined,
	);

	/**
	 * Creates a new instance, calling the "create" operation on the Jade controller, and returning an EditDto.
	 * The CREATE part of CRUD.
	 * @param type
	 * @param data
	 * @returns
	 */
	create(type: string, data?: Record<string, unknown>);

	/**
	 * Selects an instance for editing. The READ part of CRUD.
	 * @param type
	 * @param oid
	 * @returns
	 */
	select(type: string, oid?: string, data?: Record<string, unknown>);

	/**
	 * Saves changes to an instance. The UPDATE part of CRUD.
	 * @param type
	 * @param edition
	 * @param oid
	 * @param model
	 * @param opts
	 * @returns
	 */
	save<TEditDto extends EditDto>(
		type: string,
		edition: number,
		oid: string,
		model: TEditDto,
		opts?: Record<string, unknown>,
	): Observable<AggregateResultDto>;

	/**
	 * Deletes an instance. The DELETE part of CRUD.
	 * @param type
	 * @param oid
	 * @param opts
	 * @returns
	 */
	delete(type: string, oid: string, opts?: Record<string, unknown>);
}

/**
 * A base implementation of ICrudAdaptor that just defers to the standard CrudService for all operations.
 */
export abstract class BaseCrudAdaptor implements ICrudAdaptor {
	constructor(protected crud: CrudService) {}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	open<T extends ScreenConfigDto = ScreenConfigDto, TController = UseCaseController>(
		type: string,
		lookupTypes?: string[] | undefined,
		opts?: TController | undefined,
	) {
		return this.crud.open(type, lookupTypes, opts);
	}

	create(type: string, data?: Record<string, unknown> | undefined) {
		return this.crud.create(type, data);
	}

	select(type: string, oid?: string | undefined, data?: Record<string, unknown> | undefined) {
		return this.crud.select(type, oid, data);
	}

	save<TEditDto extends EditDto>(
		type: string,
		edition: number,
		oid: string,
		model: TEditDto,
		opts?: Record<string, unknown> | undefined,
	): Observable<AggregateResultDto> {
		return this.crud.save(type, edition, oid, model, opts);
	}

	delete(type: string, oid: string, opts?: Record<string, unknown> | undefined) {
		return this.crud.delete(type, oid, opts);
	}
}
